import { AppStates } from './app';
import { TabType, isPopupPanelID, tabFocusSelf } from './pages';

const failed = (msg: string) => {
  throw new Error(`Check failed: ${msg}`);
};

const uniqueArray = <T>(array: T[]): T[] => {
  return array.filter((val, index) => array.indexOf(val) === index);
};

// PopupInfoやTabInfoに含まれるpathは, httpなどのschemeを含まないこと（常にpublicationからの相対）
const checkPathsDoNotIncludeScheme = (state: Readonly<AppStates>) => {
  const { pages } = state;
  for (const tab of pages.tabs) {
    if (tab.type === TabType.ExtraPage) {
      if (tab.path.indexOf(':') !== -1) {
        failed('ExtraPage TabInfo must not have absolute URL.');
      }
    }
  }

  for (const popupID in pages.popups) {
    if (pages.popups[popupID].path.indexOf(':') !== -1) {
      failed('PopupInfo must not have absolute URL.');
    }
  }
};

// PagesStateのtabIDはunique
const checkTabIDsAreUnique = (state: Readonly<AppStates>) => {
  const { pages } = state;
  const tabIDs = pages.tabs.map(t => t.id);
  if (uniqueArray(tabIDs).length !== tabIDs.length) {
    failed('Duplicate tabID found.');
  }
};

// PagesStateのtab関連雑多なチェック
const checkTabsMisc = (state: Readonly<AppStates>) => {
  const { pages } = state;

  if (pages.activeTab > 0 && !pages.tabs[pages.activeTab]) { // MainTabを開く前は無視
    failed('Active tab index is invalid.');
  }
  if (pages.cumulativeTabCount < pages.tabs.length) {
    failed('cumulativeTabCount is smaller than the tab count.');
  }

  // tabIDに変な値がいないこと（うっかりundefinedなどが混じるのを早期検知）
  // なおAPI仕様上、keyに指定できるのは英数字のみとし、"undefined"や"null"などはNGワードとする
  for (const tab of pages.tabs) {
    const { id } = tab;
    if (!id) {
      failed('tabID is null.');
    }
    if (id.indexOf('undefined') !== -1 || id.indexOf('null') !== -1 || id[id.length - 1] === '-') {
      failed(`Invalid tabID found: ${id}`);
    }

    // tab.focusが存在しないポップアップを指していないこと
    if (tab.focus !== tabFocusSelf && !(tab.focus in pages.popups)) {
      failed(`Unexpeced tab focus: ${tab.focus}`);
    }
  }
};

// ViewStateに入っているIDは、必ずPagesStateのどれかのタブをさす
const checkNoDanglingViewStatesExist = (state: Readonly<AppStates>) => {
  const { view, pages } = state;
  for (const id in view.tabStates) {
    if (isPopupPanelID(id)) {
      if (!(id in pages.popups)) {
        failed('Dangling SingleViewState found: popup');
      }
    } else {
      if (!pages.tabs.some(t => t.id === id)) {
        failed(`Dangling SingleViewState found: ${id}`);
      }
    }
  }

  for (const id in view.frameContentSizes) {
    if (isPopupPanelID(id)) {
      if (!(id in pages.popups)) {
        failed('Dangling frameContentSize found: popup');
      }
    } else {
      if (!pages.tabs.some(t => t.id === id)) {
        failed(`Dangling frameContentSize found: ${id}`);
      }
    }
  }
};

// tabPanelAreaRectは常にundefinedではない
// popup表示中はpopupRectがundefinedではない、またpopup非表示時は不要なのでセットされていない
const checkViewContainerSizeAvailable = (state: Readonly<AppStates>) => {
  const { view, pages } = state;
  for (const popupID in pages.popups) {
    if (view.popupRects[popupID] == null) {
      failed('popupRect is unavailable while popup is shown.');
    }
  }
  for (const popupID in view.popupRects) {
    if (!(popupID in pages.popups)) {
      failed('popupRect is set while popup is not shown.');
    }
  }
};

export const verifyAppStates = (state: Readonly<AppStates>) => {
  checkPathsDoNotIncludeScheme(state);
  checkTabIDsAreUnique(state);
  checkTabsMisc(state);
  checkNoDanglingViewStatesExist(state);
  checkViewContainerSizeAvailable(state);
};
